// src/components/SimpleFormComponents/SimplePhone.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { Form, Select } from 'antd';
import PhoneInput from '../JarvislyComponents/PhoneInput';
import SimpleFormItem from './SimpleFormItem';
import useFormElement from 'hooks/useFormElement';
import useCustomValidationStatus from 'hooks/useCustomValidationStatus';
import { useSimpleFormContext } from './SimpleFormProvider';
import { getStaticLocaleFromApp } from 'utils/localeUtils';
import { returnOnlyNumbers } from 'utils/helpers';
import { formatPhoneNumberByCountry } from 'utils/phoneFormatter';
import { useDocumentContext } from 'components/core/DocumentProvider';
import {
  getPhoneMeta,
  isValidPhoneNumber,
  getAllCountriesMetadata,
  getCountryMetadata,
  getFlag,
} from 'utils/phone';
import { normalizeCountryISO } from 'utils/locale';
import useZustand from 'hooks/useZustand';
import getValueFromFlattenObj from 'utils/getValueFromFlattenObj';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const normalize = (s = '') =>
  s
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const FLAG_WRAPPER_STYLE = {
  fontSize: 24,
  lineHeight: '24px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  textAlign: 'center',
};

const FLAG_IMG_STYLE = { width: 24, height: 16, objectFit: 'cover' };
const SELECT_COUNTRY_STYLE = { width: 68, borderRight: 'none' };

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimplePhone = ({
  name,
  subdocumentField,
  style = {},
  styleItem = {},
  disabled = false,
  prefix,
  international = false,
  countryCode: countryCodeProp,
  countryNamesLang: countryNamesLangProps,
  countrySelectPlacement = 'left',
  availableCountries,
  onCountryChange,
  disableCountrySelect = false,
  countrySelectPopupWidth,
  label,
  profile,
  ...props
}) => {
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  const storedLanguage = useZustand('theme', 'language');
  const { locked, setAdditionalFields, form, disabledFields, initialValues } =
    useSimpleFormContext();
  const { data } = useDocumentContext();

  // 2. HOOKS & REFS
  const selectRef = useRef(null);
  const elementProps = useFormElement(name, subdocumentField);
  const {
    elementRef,
    fullFieldName,
    flattenFieldName,
    fullParentFieldName,
    flattenParentFieldName,
  } = elementProps;

  // 3. LOCAL VARIABLES I
  if (!label) label = profile;
  const isDisabled = disabled || locked || disabledFields?.includes(flattenFieldName);

  // Helper to get countryCode field name/path.
  // Virtual fields: contacts_.mobile.value -> contacts_.mobile.countryCode
  // Simple fields:  phone -> phoneCountryCode
  const countryCodeFieldName = useMemo(() => {
    if (fullParentFieldName) {
      return Array.isArray(fullParentFieldName)
        ? [...fullParentFieldName, 'countryCode']
        : `${fullParentFieldName}.countryCode`;
    }
    return `${fullFieldName}CountryCode`;
  }, [fullFieldName, fullParentFieldName]);

  const countryCodeFlattenFieldName = useMemo(() => {
    if (flattenParentFieldName) {
      return `${flattenParentFieldName}.countryCode`;
    }
    return `${flattenFieldName}CountryCode`;
  }, [flattenFieldName, flattenParentFieldName]);

  const appDefaultCountry = useMemo(() => {
    const staticLocale = getStaticLocaleFromApp();
    const cc = getStaticLocaleFromApp(staticLocale, true);
    return (cc || 'US').toUpperCase();
  }, []);

  // Effective countryCode precedence:
  // 1. phone.countryCode from database (via flattenFieldName)
  // 2. initialValues.phone.countryCode (initial phone values)
  // 3. data.countryCode (form country from database)
  // 4. countryCodeProp (passed from parent, e.g., form countryCode state)
  // 5. appDefaultCountry (app locale)
  const effectiveDefaultCountryCode = useMemo(() => {
    const phoneCountryCodeFromDb = getValueFromFlattenObj(data, countryCodeFlattenFieldName);

    let initialCountryCode;
    if (Array.isArray(countryCodeFieldName)) {
      initialCountryCode = countryCodeFieldName.reduce((obj, key) => obj?.[key], initialValues);
    } else {
      initialCountryCode = initialValues?.[countryCodeFieldName];
    }

    // form country (fallback)
    const formCountryCode = data?.countryCode;

    const resolved =
      phoneCountryCodeFromDb ||
      initialCountryCode ||
      formCountryCode ||
      countryCodeProp ||
      appDefaultCountry ||
      'US';

    return resolved?.toUpperCase() || 'US';
  }, [
    data,
    countryCodeFlattenFieldName,
    countryCodeProp,
    appDefaultCountry,
    countryCodeFieldName,
    initialValues,
  ]);

  const countryNamesLang = normalizeCountryISO(
    countryNamesLangProps || storedLanguage || effectiveDefaultCountryCode,
    'iso'
  );

  const allCountriesData = useMemo(
    () => getAllCountriesMetadata(countryNamesLang),
    [countryNamesLang]
  );

  const allCountryCodes = useMemo(
    () => allCountriesData?.map(c => c.countryCode),
    [allCountriesData]
  );

  const allowedCodes = useMemo(
    () =>
      (Array.isArray(availableCountries) && availableCountries.length > 0
        ? availableCountries
        : allCountryCodes
      ).map(c => c.toUpperCase()),
    [availableCountries, allCountryCodes]
  );

  const countryMap = useMemo(() => {
    const map = {};
    for (const c of allCountriesData) {
      map[c.countryCode.toUpperCase()] = c;
    }
    return map;
  }, [allCountriesData]);

  const selectOptions = useMemo(() => {
    return allowedCodes
      .map(code => countryMap[code])
      .filter(Boolean)
      .map(c => {
        const name =
          c.names?.[countryNamesLang] || c.names?.pt || c.names?.en || c.countryCode.toUpperCase();

        // SVG flag from local /img/flags/ - works on all platforms (no emoji font dependency).
        // Emoji flags are intentionally avoided: on Windows the emoji font does not include
        // country flag glyphs and renders them as letter pairs ("BR", "PT") instead of flags.
        const flagSrc = getFlag(c.countryCode);
        const flagContent = flagSrc ? (
          <img
            src={flagSrc}
            alt={c.countryCode}
            style={FLAG_IMG_STYLE}
            onError={e => { e.target.style.display = 'none'; }}
          />
        ) : (
          c.countryCode.toUpperCase()
        );

        const Flag = <span style={FLAG_WRAPPER_STYLE}>{flagContent}</span>;

        const labelDropdown = (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {Flag}
            <span>
              {name} +{c.dial}
            </span>
          </span>
        );

        const labelSelected = (
          <span style={{ display: 'inline-flex', alignItems: 'center', marginTop: 6 }}>
            {Flag}
          </span>
        );

        const searchText = normalize(
          `${name} ${c.names?.pt} ${c.names?.en} ${c.names?.es} ${c.countryCode} +${c.dial}`
        );

        return {
          value: c.countryCode,
          label: labelDropdown,
          'data-label-short': labelSelected,
          'data-search-text': searchText,
        };
      });
  }, [allowedCodes, countryMap, countryNamesLang]);

  // 4. LOCAL STATE
  const [selectedCountryCode, setSelectedCountryCode] = useState(effectiveDefaultCountryCode);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Watch phone.countryCode from form to sync when parent updates it
  const formPhoneCountryCode = Form.useWatch(countryCodeFieldName, form);

  // 5. LOCAL VARIABLES II
  const { customValidationStatus, customValidationMessage, handleChange } =
    useCustomValidationStatus(
      {
        ...props,
        ...elementProps,
        isPhone: true,
        args: {
          profile,
          countryCode: selectedCountryCode,
        },
      },
      isValidPhoneNumber
    );

  // 5. EFFECTS I
  // Initialize selectedCountryCode and form field with effectiveDefaultCountryCode
  // ONLY when both phone.value and phone.countryCode are empty
  useEffect(() => {
    const currentFormValue = form?.getFieldValue(countryCodeFieldName);
    const phoneValue = form?.getFieldValue(fullFieldName);
    const phoneIsEmpty = !phoneValue || String(phoneValue).trim() === '';

    const shouldInitialize =
      phoneIsEmpty &&
      (currentFormValue === undefined || currentFormValue === null || currentFormValue === '');

    if (shouldInitialize) {
      if (selectedCountryCode !== effectiveDefaultCountryCode) {
        setSelectedCountryCode(effectiveDefaultCountryCode);
      }
      if (Array.isArray(countryCodeFieldName)) {
        const fieldValue = countryCodeFieldName.reduceRight(
          (acc, key) => ({ [key]: acc }),
          effectiveDefaultCountryCode
        );
        form?.setFieldsValue?.(fieldValue);
      } else {
        form?.setFieldsValue?.({ [countryCodeFieldName]: effectiveDefaultCountryCode });
      }
    }
  }, [effectiveDefaultCountryCode, selectedCountryCode, form, countryCodeFieldName, fullFieldName]);

  // Sync with form's phone.countryCode when it changes (e.g., from SimpleDataOriginForm)
  useEffect(() => {
    if (!formPhoneCountryCode) return;

    const normalizedCode = formPhoneCountryCode.toUpperCase();
    const isValidCode =
      normalizedCode &&
      normalizedCode.length === 2 &&
      allowedCodes.includes(normalizedCode) &&
      normalizedCode !== selectedCountryCode;

    if (isValidCode) {
      setSelectedCountryCode(normalizedCode);
    }
  }, [formPhoneCountryCode, selectedCountryCode, allowedCodes]);

  // Follow the PERSISTED phone country once a saved record loads.
  // selectedCountryCode's useState init runs BEFORE the document data arrives, so
  // it seeds from the app/subscription default (e.g. PT). A BR phone saved with
  // countryCode 'BR' would then keep the PT flag -> wrong format + "invalid".
  // Re-sync to the DB phone.countryCode exactly once per loaded record (a value
  // is present, so this never fights the empty-phone -> SimpleCountry rule).
  const loadedRecordCountrySyncRef = useRef(null);
  useEffect(() => {
    const persistedCountryCode = getValueFromFlattenObj(data, countryCodeFlattenFieldName);
    if (!persistedCountryCode) return;

    const recordKey = data?._id ? String(data._id) : '';
    if (loadedRecordCountrySyncRef.current === recordKey) return;
    loadedRecordCountrySyncRef.current = recordKey;

    const cc = String(persistedCountryCode).toUpperCase();
    if (cc.length !== 2 || !allowedCodes.includes(cc) || cc === selectedCountryCode) return;

    setSelectedCountryCode(cc);
    const parentField = fullParentFieldName || fullFieldName;
    const currentValue = form?.getFieldValue(parentField) || {};
    form?.setFieldValue(parentField, { ...currentValue, countryCode: cc });
  }, [
    data,
    countryCodeFlattenFieldName,
    allowedCodes,
    selectedCountryCode,
    fullParentFieldName,
    fullFieldName,
    form,
  ]);

  // Sync with parent countryCode (from SimpleCountry) when phone is empty.
  // Rule: SimpleCountry -> SimplePhone (when phone is empty), never the reverse.
  useEffect(() => {
    if (!countryCodeProp) return;

    const phoneFieldValue = form?.getFieldValue(fullFieldName);
    const actualPhoneValue =
      phoneFieldValue && typeof phoneFieldValue === 'object'
        ? phoneFieldValue.value
        : phoneFieldValue;
    const phoneIsEmpty = !actualPhoneValue || String(actualPhoneValue).trim() === '';

    if (phoneIsEmpty) {
      const normalizedProp = countryCodeProp.toUpperCase();

      if (normalizedProp.length !== 2) return;

      if (selectedCountryCode !== normalizedProp) {
        setSelectedCountryCode(normalizedProp);

        const parentField = fullParentFieldName || fullFieldName;
        const currentValue = form?.getFieldValue(parentField) || {};
        form?.setFieldValue(parentField, {
          ...currentValue,
          countryCode: normalizedProp,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCodeProp]);

  // 6. HANDLERS
  const handleCountryChange = useCallback(
    val => {
      const cc = String(val || 'US').toUpperCase();
      setSelectedCountryCode(cc);

      const parentField = fullParentFieldName || fullFieldName;
      const currentValue = form?.getFieldValue(parentField) || {};
      form?.setFieldValue(parentField, {
        ...currentValue,
        countryCode: cc,
      });

      // External callback (if provided)
      onCountryChange && onCountryChange(cc);

      setDropdownOpen(false);
    },
    [onCountryChange, fullParentFieldName, fullFieldName, form]
  );

  const handleDropdownVisibleChange = useCallback(visible => {
    setDropdownOpen(visible);
  }, []);

  const handleInputKeyDown = useCallback(
    ev => {
      if (
        ev.key === 'Tab' &&
        ev.shiftKey &&
        international &&
        !disableCountrySelect &&
        !locked &&
        !disabled
      ) {
        ev.preventDefault();
        const sel = selectRef.current;
        if (sel && typeof sel.focus === 'function') {
          sel.focus();
          if (typeof sel?.open === 'boolean') {
            const e = new KeyboardEvent('keydown', { key: 'Enter', bubbles: true });
            sel?.popupRef ? sel.popupRef.dispatchEvent(e) : sel?.focus();
          }
        }
      }
    },
    [international, disableCountrySelect, locked, disabled]
  );

  const handlePhoneChange = useCallback(
    (ev, metaProfile, metaObj) => {
      const parentField = fullParentFieldName || fullFieldName;
      const currentValue = form?.getFieldValue(parentField) || {};

      const { number, ...cleanedValue } = currentValue;

      const rawValue = ev?.target?.value || '';
      const rawDigits = rawValue.replace(/\D/g, '');
      const phoneValue = metaObj?.phone || rawDigits;

      form?.setFieldValue(parentField, {
        ...cleanedValue,
        ...(metaObj || {}),
        countryCode: selectedCountryCode,
        value: phoneValue,
      });

      const syntheticEvent = {
        target: { value: phoneValue },
        __originalEvent: ev,
        __profile: metaProfile,
        __metaObj: metaObj,
      };

      handleChange(syntheticEvent, metaProfile, metaObj);
    },
    [handleChange, fullFieldName, fullParentFieldName, form, selectedCountryCode]
  );

  // 7. LOCAL VARIABLES III
  const wrapperRef = useRef(null);

  // Note: not using useMemo here - would cause dropdown to close when selectedCountryCode changes
  const countrySelect = international ? (
    <div
      ref={wrapperRef}
      className="country-select-hover-wrapper"
      onMouseDown={e => {
        e.stopPropagation();
      }}
    >
      <Select
        ref={selectRef}
        value={selectedCountryCode}
        options={selectOptions}
        className="select-with-icon"
        showSearch
        open={dropdownOpen}
        onOpenChange={handleDropdownVisibleChange}
        style={SELECT_COUNTRY_STYLE}
        labelRender={labelProps => labelProps['data-label-short'] || labelProps.label}
        popupMatchSelectWidth={false}
        styles={{
          popup: {
            root: countrySelectPopupWidth
              ? { width: countrySelectPopupWidth, minWidth: countrySelectPopupWidth }
              : { minWidth: 'max-content' },
          },
        }}
        filterOption={(input, option) => {
          const q = normalize(input);
          const txt = normalize(option?.['data-search-text'] || '');
          return txt.includes(q);
        }}
        virtual={false}
        onChange={handleCountryChange}
        tabIndex={-1}
        disabled={isDisabled || disableCountrySelect}
        getPopupContainer={() => wrapperRef.current || document.body}
      />
    </div>
  ) : null;

  const statusForSuffix = useMemo(() => {
    if (!customValidationStatus) return '';
    if (customValidationStatus === 'validating') return 'validating';
    if (customValidationStatus === 'success') return 'success';
    if (customValidationStatus === 'error') return 'error';
    return '';
  }, [customValidationStatus]);

  // 8. EFFECTS II

  // 9. UI
  return (
    <SimpleFormItem
      {...props}
      name={fullFieldName}
      elementProps={elementProps}
      customValidationStatus={customValidationStatus}
      customValidationMessage={customValidationMessage}
      style={styleItem}
      label={label}
      showFeedback={false}
      isPhone={true}
    >
      <PhoneInput
        {...props}
        ref={elementRef}
        id={flattenFieldName}
        style={style}
        onKeyDown={handleInputKeyDown}
        onChange={handlePhoneChange}
        disabled={isDisabled}
        prefix={prefix}
        countryCode={selectedCountryCode}
        addonBefore={
          international && countrySelectPlacement !== 'right' ? countrySelect : undefined
        }
        addonAfter={international && countrySelectPlacement === 'right' ? countrySelect : undefined}
        international={international}
        profile={profile}
        status={statusForSuffix}
        loading={false}
      />
    </SimpleFormItem>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimplePhone;

// *******************************************************************************
// HELPERS
// *******************************************************************************
